// Question Generator - Repo-faithful implementation based on Angular logic
// This replaces static questions with dynamic generation using strings.properties data and Angular helper logic

import { selectionData } from "./selectionData";
import defaultOptionOrdering from "./default/option_ordering.json";
import zhCnOptionOrdering from "./zh_cn/option_ordering.json";
import { getAllStrings } from "../utils/localizationHelper";

// Build strings object from localized properties files
// This maintains backward compatibility while enabling i18n
function buildStringsFromLocale() {
  const allStrings = getAllStrings();

  // Build quiz strings structure from flat key-value pairs
  const quizStrings = {};

  // Group quiz.* keys into nested structure
  Object.keys(allStrings).forEach((key) => {
    if (key.startsWith("quiz.")) {
      const parts = key.replace("quiz.", "").split(".");

      if (parts.length === 2) {
        // quiz.q1.label -> quizStrings.q1.label
        const [questionKey, property] = parts;
        if (!quizStrings[questionKey]) {
          quizStrings[questionKey] = {};
        }
        quizStrings[questionKey][property] = allStrings[key];
      } else if (parts.length === 3) {
        // quiz.q1.colorimetric.tooltip -> quizStrings.q1.colorimetric_tooltip
        const [questionKey, optionKey, property] = parts;
        if (!quizStrings[questionKey]) {
          quizStrings[questionKey] = {};
        }
        const combinedKey = property ? `${optionKey}_${property}` : optionKey;
        quizStrings[questionKey][combinedKey] = allStrings[key];
      } else if (parts.length === 1) {
        // quiz.skip_unsure -> quizStrings.skip_unsure
        quizStrings[parts[0]] = allStrings[key];
      }
    }
  });

  return { quiz: quizStrings };
}

// Strings data structure - now built from localization system
// Maintains same structure as before for backward compatibility
export const strings = buildStringsFromLocale();

// Helper functions - ported from Angular quiz.helper.js and data.helper.js
export const quizHelper = {
  // Extract answers from state for query building (Angular extractQueryAnswers)
  extractQueryAnswers(keyPrefix, answers, currentQuestionKey) {
    const query = {};

    Object.keys(answers).forEach((key) => {
      // Only include questions before the current one
      if (currentQuestionKey != null) {
        // Extract question numbers for proper comparison (q1 -> 1, q5 -> 5, etc.)
        const currentNum = parseInt(currentQuestionKey.replace("q", ""));
        const keyNum = parseInt(key.replace("q", ""));
        if (keyNum >= currentNum) {
          return;
        }
      }

      if (key.startsWith(keyPrefix)) {
        const value = Array.isArray(answers[key])
          ? answers[key][0]
          : answers[key];
        if (value != null && value !== "skipped") {
          query[key.substr(keyPrefix.length)] = value;
        }
      }
    });

    return query;
  },

  // Filter data based on query criteria (Angular runQuery)
  runQuery(data, query) {
    return data.filter((row) => {
      let match = true;

      Object.keys(query).forEach((key) => {
        const queryValue = query[key];
        const rowValue = row[key];

        if (Array.isArray(rowValue)) {
          if (!rowValue.includes(queryValue)) {
            match = false;
            return;
          }
        } else if (rowValue !== queryValue && rowValue !== "*") {
          match = false;
          return;
        }
      });

      return match;
    });
  },

  // Main function to check if a question option should be visible
  // Based on Angular isQuestionOptionVisible function
  isQuestionOptionVisible(question, option, answers, data) {
    let visible = true;

    if (data != null) {
      const keyPrefix = "";
      const query = this.extractQueryAnswers(keyPrefix, answers, question.key);
      const remainingData = this.runQuery(data, query);
      const remainingOptions = remainingData
        .map((row) => row[question.key.substr(keyPrefix.length)])
        .flat();
      const uniqueOptions = [...new Set(remainingOptions)];
      visible = uniqueOptions.includes(option.key);
    }

    return visible;
  },

  // Extract all valid options for questions
  extractQuestionOptions(data, keys) {
    const options = {};

    keys.forEach((key) => {
      options[key] = [
        ...new Set(
          data
            .map((row) => row[key])
            .flat()
            .filter((val) => val != null && val !== "*"),
        ),
      ].sort();
    });

    return options;
  },

  // Get question text based on ELISA type
  getQuestionText(questionKey, elisaType) {
    if (questionKey === "q4") {
      // Q4 text changes based on Q3 (ELISA type)
      if (elisaType === "direct" || elisaType === "indirect") {
        return strings.quiz.q4.text_b; // "How will your target analyte bind to the plate?"
      } else {
        return strings.quiz.q4.text_a; // "How will your capture antibody bind to the plate?"
      }
    }
    return strings.quiz[questionKey]?.text || "";
  },
};

// Data helper functions - ported from Angular data.helper.js
export const dataHelper = {
  // Get selections data - equivalent to Angular getSelectionsData
  getSelectionsData() {
    return selectionData;
  },

  // Sanitize objects - clean and normalize data
  sanitizeObjects(objects) {
    if (!Array.isArray(objects)) {
      return [];
    }

    return objects.map((obj) => {
      const sanitized = {};
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value && typeof value === "string") {
          // Clean up string values
          sanitized[key] = value.trim().toLowerCase().replace(/\s+/g, "-");
        } else {
          sanitized[key] = value;
        }
      });
      return sanitized;
    });
  },

  // Run query on data - wrapper around quizHelper.runQuery
  runQuery(query, data) {
    return quizHelper.runQuery(query, data);
  },
};

// Utility to get option priority map from imported JSON
function getOptionPriorityMap(locale = "default") {
  const csvData =
    locale === "zh_cn" ? zhCnOptionOrdering : defaultOptionOrdering;
  const map = {};
  for (const row of csvData) {
    const { question, value, priority } = row;
    if (!question || !value) continue;
    if (!map[question]) map[question] = {};
    map[question][value] = parseInt(priority);
  }
  return map;
}

// Sort options by priority from CSV
function sortOptionsByPriority(questionKey, options, locale = "default") {
  const priorityMap = getOptionPriorityMap(locale);
  if (!priorityMap[questionKey]) return options;
  return [...options].sort((a, b) => {
    const pa = priorityMap[questionKey][a.key] ?? 999;
    const pb = priorityMap[questionKey][b.key] ?? 999;
    return pa - pb;
  });
}

// Question definition structure - matches Angular question structure but uses dynamic logic
export const questionDefinitions = {
  q1: {
    key: "q1",
    type: "options",
    allow_skip: false,
    multiple: false,
    getLabel: () => strings.quiz.q1.label,
    getText: () => strings.quiz.q1.text,
    getBaseOptions: () => [
      {
        key: "colorimetric",
        getLabel: () => strings.quiz.q1.colorimetric,
        getTooltip: () => strings.quiz.q1.colorimetric_tooltip,
      },
      {
        key: "fluorescent",
        getLabel: () => strings.quiz.q1.fluorescent,
        getTooltip: () => strings.quiz.q1.fluorescent_tooltip,
      },
      {
        key: "chemiluminescent",
        getLabel: () => strings.quiz.q1.chemiluminescent,
        getTooltip: () => strings.quiz.q1.chemiluminescent_tooltip,
      },
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q1.getBaseOptions();
      const filtered = baseOptions.filter((option) =>
        quizHelper.isQuestionOptionVisible(
          { key: "q1" },
          option,
          state,
          selectionData,
        ),
      );
      return sortOptionsByPriority("q1", filtered, locale);
    },
  },

  q2: {
    key: "q2",
    type: "options",
    allow_skip: false,
    multiple: false,
    getLabel: () => strings.quiz.q2.label,
    getText: () => strings.quiz.q2.text,
    getBaseOptions: () => [
      {
        key: "96-well",
        getLabel: () => strings.quiz.q2["96_well"],
      },
      {
        key: "96-well-8-well-inserts",
        getLabel: () => strings.quiz.q2["96_well_8_well_inserts"],
        getTooltip: () => strings.quiz.q2["96_well_8_well_inserts_tooltip"],
      },
      {
        key: "384-well",
        getLabel: () => strings.quiz.q2["384_well"],
      },
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q2.getBaseOptions();
      const filtered = baseOptions.filter((option) =>
        quizHelper.isQuestionOptionVisible(
          { key: "q2" },
          option,
          state,
          selectionData,
        ),
      );
      return sortOptionsByPriority("q2", filtered, locale);
    },
  },

  q3: {
    key: "q3",
    type: "options",
    allow_skip: false,
    multiple: false,
    getLabel: () => strings.quiz.q3.label,
    getText: () => strings.quiz.q3.text,
    getBaseOptions: () => [
      {
        key: "direct",
        getLabel: () => strings.quiz.q3.direct,
        getTooltip: () => strings.quiz.q3.direct_tooltip,
      },
      {
        key: "indirect",
        getLabel: () => strings.quiz.q3.indirect,
        getTooltip: () => strings.quiz.q3.indirect_tooltip,
      },
      {
        key: "sandwich",
        getLabel: () => strings.quiz.q3.sandwich,
        getTooltip: () => strings.quiz.q3.sandwich_tooltip,
      },
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q3.getBaseOptions();
      const filtered = baseOptions.filter((option) =>
        quizHelper.isQuestionOptionVisible(
          { key: "q3" },
          option,
          state,
          selectionData,
        ),
      );
      return sortOptionsByPriority("q3", filtered, locale);
    },
  },

  q4: {
    key: "q4",
    type: "options",
    allow_skip: true, // Q4 allows skip functionality
    skip_value: "unsure", // When skipped, answer is set to "unsure"
    multiple: false,
    getLabel: () => strings.quiz.q4.label,
    getText: (state) => {
      let q3 = state.q3;
      if (Array.isArray(q3)) {
        q3 = q3[0];
      }
      return quizHelper.getQuestionText("q4", q3);
    },
    getBaseOptions: () => [
      {
        key: "coat-antibody-directly-a",
        getLabel: () => strings.quiz.q4.coat_antibody_directly_a,
        getTooltip: () => strings.quiz.q4.coat_antibody_directly_a_tooltip,
      },
      {
        key: "coat-antibody-directly-b",
        getLabel: () => strings.quiz.q4.coat_antibody_directly_b,
      },
      {
        key: "biotinylated-streptavidin-neutravidin-a",
        getLabel: () => strings.quiz.q4.biotinylated_streptavidin_neutravidin_a,
        getTooltip: () =>
          strings.quiz.q4.biotinylated_streptavidin_neutravidin_a_tooltip,
      },
      {
        key: "biotinylated-streptavidin-neutravidin-b",
        getLabel: () => strings.quiz.q4.biotinylated_streptavidin_neutravidin_b,
        getTooltip: () =>
          strings.quiz.q4.biotinylated_streptavidin_neutravidin_b_tooltip,
      },
      {
        key: "goat-anti-mouse-igg-a",
        getLabel: () => strings.quiz.q4.goat_anti_mouse_igg_a,
        getTooltip: () => strings.quiz.q4.goat_anti_mouse_igg_a_tooltip,
      },
      {
        key: "goat-anti-mouse-igg-b",
        getLabel: () => strings.quiz.q4.goat_anti_mouse_igg_b,
      },
      {
        key: "coat-analyte-directly-a",
        getLabel: () => strings.quiz.q4.coat_analyte_directly_a,
      },
      {
        key: "coat-analyte-directly-b",
        getLabel: () => strings.quiz.q4.coat_analyte_directly_b,
        getTooltip: () => strings.quiz.q4.coat_analyte_directly_b_tooltip,
      },
      {
        key: "his-nickel-a",
        getLabel: () => strings.quiz.q4.his_nickel_a,
      },
      {
        key: "his-nickel-b",
        getLabel: () => strings.quiz.q4.his_nickel_b,
        getTooltip: () => strings.quiz.q4.his_nickel_b_tooltip,
      },
      {
        key: "his-copper-a",
        getLabel: () => strings.quiz.q4.his_copper_a,
      },
      {
        key: "his-copper-b",
        getLabel: () => strings.quiz.q4.his_copper_b,
        getTooltip: () => strings.quiz.q4.his_copper_b_tooltip,
      },
      {
        key: "gst-glutathione-a",
        getLabel: () => strings.quiz.q4.gst_glutathione_a,
      },
      {
        key: "gst-glutathione-b",
        getLabel: () => strings.quiz.q4.gst_glutathione_b,
        getTooltip: () => strings.quiz.q4.gst_glutathione_b_tooltip,
      },
      {
        key: "sulfhydryl-maleimide-a",
        getLabel: () => strings.quiz.q4.sulfhydryl_maleimide_a,
      },
      {
        key: "sulfhydryl-maleimide-b",
        getLabel: () => strings.quiz.q4.sulfhydryl_maleimide_b,
        getTooltip: () => strings.quiz.q4.sulfhydryl_maleimide_b_tooltip,
      },
      {
        key: "amine-maleic-anhydride-a",
        getLabel: () => strings.quiz.q4.amine_maleic_anhydride_a,
      },
      {
        key: "amine-maleic-anhydride-b",
        getLabel: () => strings.quiz.q4.amine_maleic_anhydride_b,
        getTooltip: () => strings.quiz.q4.amine_maleic_anhydride_b_tooltip,
      },
      // Note: "unsure" is NOT included as a visible option, it's only used as skip_value
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q4.getBaseOptions();
      // Filter out the skip_value from visible options (repo-faithful to Angular logic)
      const filtered = baseOptions.filter((option) => {
        // Don't show "unsure" as a selectable option, it's handled by skip functionality
        if (option.key === questionDefinitions.q4.skip_value) {
          return false;
        }
        return quizHelper.isQuestionOptionVisible(
          { key: "q4" },
          option,
          state,
          selectionData,
        );
      });
      return sortOptionsByPriority("q4", filtered, locale);
    },
  },

  q5: {
    key: "q5",
    type: "options",
    allow_skip: true, // Q5 allows skip functionality
    skip_value: "unsure", // When skipped, answer is set to "unsure"
    multiple: false,
    getLabel: () => strings.quiz.q5.label,
    getText: () => strings.quiz.q5.text,
    getBaseOptions: () => [
      {
        key: "high",
        getLabel: () => strings.quiz.q5.high,
      },
      {
        key: "medium",
        getLabel: () => strings.quiz.q5.medium,
      },
      {
        key: "low",
        getLabel: () => strings.quiz.q5.low,
      },
      // Note: "unsure" is NOT included as a visible option, it's only used as skip_value
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q5.getBaseOptions();
      // Filter out the skip_value from visible options (repo-faithful to Angular logic)
      const filtered = baseOptions.filter((option) => {
        // Don't show "unsure" as a selectable option, it's handled by skip functionality
        if (option.key === questionDefinitions.q5.skip_value) {
          return false;
        }
        return quizHelper.isQuestionOptionVisible(
          { key: "q5" },
          option,
          state,
          selectionData,
        );
      });
      return sortOptionsByPriority("q5", filtered, locale);
    },
  },

  q6: {
    key: "q6",
    type: "options",
    allow_skip: true,
    multiple: false,
    getLabel: () => strings.quiz.q6.label,
    getText: () => strings.quiz.q6.text,
    getBaseOptions: () => [
      {
        key: "hrp",
        getLabel: () => strings.quiz.q6.hrp,
        getTooltip: () => strings.quiz.q6.hrp_tooltip,
      },
      {
        key: "ap",
        getLabel: () => strings.quiz.q6.ap,
        getTooltip: () => strings.quiz.q6.ap_tooltip,
      },
      {
        key: "biotinylated-hrp-streptavidin",
        getLabel: () => strings.quiz.q6.biotinylated_hrp_streptavidin,
        getTooltip: () => strings.quiz.q6.biotinylated_hrp_streptavidin_tooltip,
      },
      {
        key: "biotinylated-ap-streptavidin",
        getLabel: () => strings.quiz.q6.biotinylated_ap_streptavidin,
        getTooltip: () => strings.quiz.q6.biotinylated_ap_streptavidin_tooltip,
      },
      {
        key: "fluorophore",
        getLabel: () => strings.quiz.q6.fluorophore,
        getTooltip: () => strings.quiz.q6.fluorophore_tooltip,
      },
    ],
    getOptions: (state, locale = "default") => {
      const baseOptions = questionDefinitions.q6.getBaseOptions();
      const filtered = baseOptions.filter((option) =>
        quizHelper.isQuestionOptionVisible(
          { key: "q6" },
          option,
          state,
          selectionData,
        ),
      );
      return sortOptionsByPriority("q6", filtered, locale);
    },
  },
};

// Main question generation function - repo-faithful to Angular implementation
// Accepts locale for option ordering
export const generateQuestions = (answersState = {}, locale = "default") => {
  // console.log("generateQuestions called with:", answersState);

  // Convert React answers state to Angular format (arrays)
  const angularAnswers = {};
  Object.keys(answersState).forEach((key) => {
    const value = answersState[key];
    if (Array.isArray(value)) {
      angularAnswers[key] = value;
    } else if (value) {
      angularAnswers[key] = [value];
    }
  });

  // console.log("Angular format answers:", angularAnswers);

  const questions = [];
  let questionNumber = 1;

  // Process each question definition
  Object.keys(questionDefinitions).forEach((questionKey) => {
    const questionDef = questionDefinitions[questionKey];
    if (!questionDef) return;

    // Use the question's getOptions method which includes skip_value filtering and ordering
    const filteredOptions = questionDef.getOptions(angularAnswers, locale);

    // Build the question object with filtered options
    const question = {
      key: questionKey,
      number: questionNumber++,
      type: questionDef.type,
      allow_skip: questionDef.allow_skip,
      skip_value: questionDef.skip_value, // Include skip_value for skip logic
      multiple: questionDef.multiple,
      label: questionDef.getLabel(),
      text:
        typeof questionDef.getText === "function"
          ? questionDef.getText(angularAnswers)
          : questionDef.getText(),
      options: filteredOptions.map((option) => ({
        key: option.key,
        text: option.getLabel(),
        tooltip: option.getTooltip ? option.getTooltip() : undefined,
      })),
      // Add filtering function like Angular does
      isOptionVisible: (optionKey) => {
        const option = { key: optionKey };
        return quizHelper.isQuestionOptionVisible(
          { key: questionKey },
          option,
          angularAnswers,
          selectionData,
        );
      },
    };

    questions.push(question);
  });

  // console.log("Final questions:", questions);
  return questions;
};

// Export default generateQuestions for compatibility
export default generateQuestions;

// Product mapping - basic implementation for results page
export const productMapping = {
  // Default fallback products for any answer combination
  default: {
    bestSolution: [
      {
        type: "product",
        category: "elisa-kits",
        product: {
          name: "Pierce Rapid ELISA Kit",
          sku: "23235",
          formattedSku: "23235",
          brand: "Thermo Scientific™",
          size: "96 tests",
          url: "https://www.thermofisher.com/order/catalog/product/23235",
          image:
            "https://assets.thermofisher.com/TFS-Assets/LSG/product-images/23235_Pierce_ELISA_Kit_Hero.jpg",
          description:
            "Versatile ELISA kit suitable for various protein detection applications with rapid workflow.",
          price_data: {
            priceString: "$315.00",
            price: 315.0,
            userPriceString: "$315.00",
            userPrice: 315.0,
            isLink: false,
            pricingCurrencyCode: "USD",
            canAddToCart: true,
            action: "Add to Cart",
          },
          specifications: {
            "Plate Format": "96-well",
            Detection: "Colorimetric",
            "Assay Time": "3 hours",
            "Sample Type": "Various",
          },
        },
      },
    ],
    additionalProducts: [
      {
        type: "product",
        category: "plates",
        product: {
          name: "Nunc MaxiSorp 96-Well Plates",
          sku: "44-2404-21",
          formattedSku: "44-2404-21",
          brand: "Thermo Scientific™",
          size: "50 plates",
          url: "https://www.thermofisher.com/order/catalog/product/44-2404-21",
          image:
            "https://assets.thermofisher.com/TFS-Assets/LSG/product-images/44-2404-21_MaxiSorp_Plates_Hero.jpg",
          description:
            "High-binding capacity 96-well microplates for ELISA applications.",
          price_data: {
            priceString: "$125.00",
            price: 125.0,
            userPriceString: "$125.00",
            userPrice: 125.0,
            isLink: false,
            pricingCurrencyCode: "USD",
            canAddToCart: true,
            action: "Add to Cart",
          },
          specifications: {
            "Well Volume": "400 μL",
            Color: "Clear",
            "Binding Capacity": "High",
            Package: "50 plates",
          },
        },
      },
    ],
  },
};
